import './styles.scoped.css'
import {useState, useEffect} from 'react'
import {Typography, Button, Chip, IconButton, Menu, MenuItem, Avatar, Skeleton} from '@mui/material'
import {Add, MoreVert} from '@mui/icons-material'
import {useParams} from 'react-router-dom'
import {format, parseISO} from 'date-fns'
import {useMobile} from '../../hooks'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {clientApi, caregiverApi} from '../../services'
import {parseError, getInitials, getColorFromName} from '../../utils/helpers'
import {AssignNewClient, UpdateAssignedClientService, RemoveAssignedClientDialog} from './components'
import {SkeletonContainer, TextSkeleton, AvatarSkeleton, ChipSkeleton} from '../../components'

const AssignedClientsSkeleton = () => (
    <SkeletonContainer>
        <div className="content">
            <ChipSkeleton width={48} height={32} />
            <ChipSkeleton width={110} height={32} />
            <ChipSkeleton width={130} height={32} />
        </div>
        {[0, 1, 2].map(index => (
            <div key={index}>
                <br />
                <div className="card">
                    <div className="top">
                        <div className="content">
                            <AvatarSkeleton size={40} />
                            <div className="client-profile-card">
                                <TextSkeleton width={index % 2 ? 130 : 160} fontSize={18} />
                                <TextSkeleton width={index % 2 ? 180 : 210} fontSize={16} />
                            </div>
                        </div>
                        <Skeleton variant="circular" width={24} height={24} sx={{m: 1}} />
                    </div>
                    <hr className="line" />
                    <div className="content">
                        <Typography variant="h9" color="text.secondary">
                            Assigned Service:
                        </Typography>
                    </div>
                    <div className="content">
                        <ChipSkeleton width={110} height={32} />
                        {index != 1 && <ChipSkeleton width={130} height={32} />}
                    </div>
                    <div className="content">
                        <Typography variant="h9" color="text.secondary">
                            Last Schedule:
                        </Typography>
                        <TextSkeleton width={80} />
                    </div>
                    <div className="content">
                        <Typography variant="h9" color="text.secondary">
                            Completed Schedule:
                        </Typography>
                        <TextSkeleton width={20} />
                        <Typography variant="body2" color="text.secondary" style={{marginLeft: 12, marginRight: 12}}>
                            |
                        </Typography>
                        <Typography variant="h9" color="text.secondary">
                            Active Schedule:
                        </Typography>
                        <TextSkeleton width={16} />
                    </div>
                </div>
            </div>
        ))}
    </SkeletonContainer>
)

export default () => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const [anchorEl, setAnchorEl] = useState(null)
    const [openAssignNewClient, setOpenAssignNewClient] = useState(false)
    const [openUpdateAssignedClientService, setOpenUpdateAssignedClientService] = useState(false)
    const [openRemoveAssignedClientDialog, setOpenRemoveAssignedClientDialog] = useState(false)
    const [selectedRow, setSelectedRow] = useState(null)
    const {caregiverId} = useParams()
    const {mobile} = useMobile()
    const [caregiver, setCaregiver] = useState(null)
    const [clients, setClients] = useState([])
    const [caregiverClients, setCaregiverClients] = useState([])
    const [filterdCaregiverClients, setFilterdCaregiverClients] = useState([])
    const [serviceFilters, setServiceFilters] = useState([])
    const [selectedServiceFilter, setSelectedServiceFilter] = useState('All')
    const [skeleton, setSkeleton] = useState(true)

    useEffect(() => {
        ;(async () => {
            if (branchId) {
                try {
                    setSkeleton(true)
                    const {data: data1} = await caregiverApi.getBranchCaregiver(branchId, caregiverId)
                    setCaregiver(data1)

                    const {
                        data: {
                            data: {data: data2},
                        },
                    } = await clientApi.getBranchClients(branchId, {limit: 100})
                    setClients(data2)

                    loadData(true)
                } catch (error) {
                    setSkeleton(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                }
            }
        })()
    }, [branchId])

    useEffect(() => {
        if (selectedServiceFilter == 'All') {
            setFilterdCaregiverClients(caregiverClients)
        } else {
            setFilterdCaregiverClients(caregiverClients?.filter(item => item?.filters?.includes(selectedServiceFilter)))
        }
    }, [selectedServiceFilter])

    // The first load shows the skeleton; the reload after assigning, updating or removing a client runs under the global spinner.
    const loadData = (initial = false) => {
        if (!initial) setLoading(true)
        caregiverApi
            .getCaregiverClients(branchId, caregiverId)
            .then(({data: {clients}}) => {
                if (!initial) setLoading(false)
                setSkeleton(false)
                const temp = []
                const filters = []
                if (clients) {
                    for (const client of clients) {
                        temp.push({...client, filters: client?.services?.map(item => item?.service_name)})
                        if (client?.services) {
                            for (const service of client?.services) {
                                if (!filters.includes(service?.service_name)) {
                                    filters.push(service?.service_name)
                                }
                            }
                        }
                    }
                }
                setCaregiverClients(temp)
                setFilterdCaregiverClients(temp)
                setServiceFilters(['All', ...filters?.sort()])
            })
            .catch(error => {
                if (!initial) setLoading(false)
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const menuOpen = (e, row) => {
        setAnchorEl(e.currentTarget)
        setSelectedRow(row)
    }

    const menuClose = () => {
        setAnchorEl(null)
    }

    const assignNewClient = () => {
        setOpenAssignNewClient(true)
    }

    const updateAssignedClientService = () => {
        setOpenUpdateAssignedClientService(true)
        menuClose()
    }

    const removeAssignedClient = () => {
        setOpenRemoveAssignedClientDialog(true)
        menuClose()
    }

    return (
        <>
            {mobile ? (
                <>
                    <Button variant="contained" startIcon={<Add />} fullWidth onClick={assignNewClient}>
                        Assign Client
                    </Button>
                    <br />
                </>
            ) : (
                <>
                    <div className="header">
                        <Typography variant="h6" color="text.primary">
                            Assigned Clients
                        </Typography>
                        <Button variant="contained" startIcon={<Add />} onClick={assignNewClient}>
                            Assign Client
                        </Button>
                    </div>
                </>
            )}
            <br />
            {skeleton && <AssignedClientsSkeleton />}
            <div className="content" style={{flexWrap: 'wrap'}}>
                {serviceFilters?.map((item, index) => (
                    <Chip
                        label={item}
                        color={item == selectedServiceFilter ? 'primary' : 'action.selected'}
                        variant="filled"
                        key={index}
                        className="filter"
                        onClick={e => setSelectedServiceFilter(item)}
                    />
                ))}
            </div>
            {filterdCaregiverClients?.map((item, index) => (
                <div key={index}>
                    <br />
                    <div className="card">
                        <div className="top">
                            <div className="content">
                                {item?.client?.avatar_url ? (
                                    <Avatar src={item?.client?.avatar_url} style={{width: 40, height: 40}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.client?.first_name + ' ' + item?.client?.last_name),
                                            width: 40,
                                            height: 40,
                                            fontSize: 20,
                                        }}>
                                        {getInitials(item?.client?.first_name + ' ' + item?.client?.last_name)}
                                    </Avatar>
                                )}
                                <div className="client-profile-card">
                                    <Typography variant="h7" color="text.primary" className="name">
                                        {item?.client?.first_name + ' ' + item?.client?.last_name}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" className="name">
                                        {item?.client?.email}
                                    </Typography>
                                </div>
                            </div>
                            <IconButton onClick={e => menuOpen(e, item)}>
                                <MoreVert />
                            </IconButton>
                        </div>
                        <hr className="line" />
                        <div className="content">
                            <Typography variant="h9" color="text.secondary">
                                Assigned Service:
                            </Typography>
                        </div>
                        <div className="content">
                            {item?.services?.map((item2, index2) => (
                                <Chip label={item2?.service_name} color="action.selected" variant="filled" key={index2} />
                            ))}
                        </div>
                        <div className="content">
                            <Typography variant="h9" color="text.secondary">
                                Last Schedule:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {item?.schedule_stats?.last_schedule_date
                                    ? format(parseISO(item.schedule_stats.last_schedule_date), 'MM/dd/yyyy')
                                    : '-'}
                            </Typography>
                        </div>
                        <div className="content">
                            <Typography variant="h9" color="text.secondary">
                                Completed Schedule:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {item?.schedule_stats?.completed_schedules ?? 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" style={{marginLeft: 12, marginRight: 12}}>
                                |
                            </Typography>
                            <Typography variant="h9" color="text.secondary">
                                Active Schedule:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {item?.schedule_stats?.active_schedules ?? 0}
                            </Typography>
                        </div>
                    </div>
                </div>
            ))}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                <div>
                    <MenuItem onClick={updateAssignedClientService}>Update Details</MenuItem>
                    <MenuItem onClick={removeAssignedClient} sx={theme => ({color: theme.palette.error.main})}>
                        Remove Assigned Client
                    </MenuItem>
                </div>
            </Menu>
            <AssignNewClient
                open={openAssignNewClient}
                setOpen={setOpenAssignNewClient}
                loadData={() => loadData()}
                caregiver={caregiver}
                allClients={clients}
                caregiverClients={caregiverClients}
            />
            <UpdateAssignedClientService
                open={openUpdateAssignedClientService}
                setOpen={setOpenUpdateAssignedClientService}
                data={selectedRow}
                loadData={() => loadData()}
                caregiver={caregiver}
                clients={clients}
            />
            <RemoveAssignedClientDialog
                open={openRemoveAssignedClientDialog}
                setOpen={setOpenRemoveAssignedClientDialog}
                data={selectedRow}
                loadData={() => loadData()}
            />
        </>
    )
}
